import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import AISuggestionsBanner from "../components/AISuggestionsBanner";
import { extractErrorMessage } from "../utils/acceptSuggestions";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  WizardContainer,
  WizardStep,
  AIInlineSuggestion,
  AIAutoFillButton,
  StepSummaryCard,
} from "../components/WizardComponents";
import {
  ClipboardList,
  Clock,
  Plus,
  CheckCircle2,
  Calendar,
  Users,
  FileText,
  RefreshCw,
  Edit,
  Eye,
  Target,
  TrendingUp,
  AlertTriangle,
  BarChart3,
  Award,
  Sparkles,
  Download,
} from "lucide-react";

const REVIEW_TYPES = [
  { id: "periodic", name: "Periodic Review", frequency: "Per configured cadence" },
  { id: "quarterly", name: "Quarterly Review", frequency: "Every 3 months" },
  { id: "annual", name: "Annual Review", frequency: "Once per year" },
  { id: "scheduled", name: "Scheduled Review", frequency: "As scheduled" },
  { id: "post_incident", name: "Post-Incident Review", frequency: "After major incidents" },
  { id: "pre_audit", name: "Pre-Audit Review", frequency: "Before certification audits" },
];

const STATUS_OPTIONS = [
  { id: "draft", name: "Draft", color: "bg-amber-100 text-amber-700" },
  { id: "scheduled", name: "Scheduled", color: "bg-blue-100 text-blue-700" },
  { id: "planned", name: "Planned", color: "bg-blue-100 text-blue-700" },
  { id: "in_progress", name: "In Progress", color: "bg-amber-100 text-amber-700" },
  { id: "completed", name: "Completed", color: "bg-emerald-100 text-emerald-700" },
  { id: "cancelled", name: "Cancelled", color: "bg-slate-100 text-slate-700" },
];

const WIZARD_STEPS = [
  { id: 1, title: "Review Setup", description: "Meeting details", icon: Calendar },
  { id: 2, title: "BCM Status", description: "Program performance", icon: BarChart3 },
  { id: 3, title: "Issues & Actions", description: "Outstanding items", icon: AlertTriangle },
  { id: 4, title: "Decisions", description: "Management decisions", icon: Target },
  { id: 5, title: "Minutes", description: "Review summary", icon: FileText },
];

export default function ManagementReviewPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [autoPopulating, setAutoPopulating] = useState(false);
  const [reviewMetrics, setReviewMetrics] = useState(null);
  // Organization Mission & Vision (surfaced from the org profile / onboarding enrichment)
  const [orgFoundation, setOrgFoundation] = useState({ mission: "", vision: "" });
  
  const [formData, setFormData] = useState({
    review_type: "quarterly",
    title: "",
    date: "",
    attendees: "",
    location: "",
    bcm_status_summary: "",
    bia_status: "",
    exercise_status: "",
    training_status: "",
    compliance_status: "",
    open_issues: "",
    capa_status: "",
    risk_changes: "",
    decisions_made: "",
    action_items: "",
    next_review_date: "",
    meeting_minutes: "",
    status: "scheduled",
  });

  // Mock data
  const mockReviews = [
    {
      id: "1",
      review_type: "quarterly",
      title: "Q4 2024 Management Review",
      date: "2024-12-15",
      attendees: "John Smith, Jane Doe, Mike Johnson",
      status: "completed",
      decisions_count: 5,
    },
    {
      id: "2",
      review_type: "annual",
      title: "Annual BCM Program Review 2024",
      date: "2025-01-10",
      attendees: "Executive Team",
      status: "completed",
      decisions_count: 12,
    },
    {
      id: "3",
      review_type: "quarterly",
      title: "Q1 2025 Management Review",
      date: "2025-03-15",
      attendees: "BCM Steering Committee",
      status: "scheduled",
      decisions_count: 0,
    },
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("/kpi/management-reviews");
      const data = response.data;
      setReviews(data.reviews || []);
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Surface Mission & Vision from the organization profile (captured during onboarding)
  useEffect(() => {
    api.get("/bcm-admin/organization")
      .then((res) => setOrgFoundation({ mission: res.data?.mission_statement || "", vision: res.data?.vision || "" }))
      .catch(() => {});
  }, [api]);

  // Load review metrics on mount so the Exercise Currency summary is visible
  // even before any review exists (Defect 4).
  useEffect(() => {
    api.get("/kpi/management-review-metrics")
      .then((res) => setReviewMetrics(res.data))
      .catch(() => {});
  }, [api]);

  // Auto-populate BCM status metrics from all modules
  const handleAutoPopulateMetrics = async () => {
    setAutoPopulating(true);
    try {
      const response = await api.get("/kpi/management-review-metrics");
      const metrics = response.data;
      setReviewMetrics(metrics);
      const ec = metrics.exercise_currency || {};
      const currencyLine = ec.required_frequency_months
        ? ` Exercise currency: ${ec.current} current, ${ec.approaching_due} approaching due, ${ec.overdue} overdue, ${ec.never_tested} never-tested (required every ${ec.required_frequency_months} months per ${ec.threshold_source}).`
        : "";

      const _lastRev = metrics.bcm_status_summary.last_review_date
        ? `Last review: ${new Date(metrics.bcm_status_summary.last_review_date).toLocaleDateString()}`
        : 'First management review.';
      const liveBcm = `BCM program operating at ${metrics.overall_compliance_score}% maturity level. ${metrics.bcm_status_summary.trend === 'up' ? 'Positive improvement trend.' : 'Maintaining current level.'} ${_lastRev}`;

      setFormData(prev => ({
        ...prev,
        // Preserve any AI-composed summary/risk text — append live metrics rather than overwrite (Clause 4 / Test F).
        bcm_status_summary: prev.bcm_status_summary
          ? `${prev.bcm_status_summary}\n\n--- Live system metrics ---\n${liveBcm}`
          : liveBcm,
        bia_status: metrics.bia_status.summary,
        exercise_status: metrics.exercise_status.summary + currencyLine,
        training_status: metrics.training_status.summary,
        compliance_status: metrics.compliance_status.summary,
        open_issues: metrics.open_issues_summary.summary,
        capa_status: metrics.paca_status.summary,
        risk_changes: prev.risk_changes
          ? `${prev.risk_changes}\n\n--- Live risk status ---\n${metrics.risk_status.summary}`
          : metrics.risk_status.summary,
      }));
      
      toast.success("BCM metrics auto-populated from system data!");
    } catch (error) {
      console.error("Failed to fetch metrics:", error);
      toast.error("Failed to auto-populate metrics. Using AI summary instead.");
      // Fall back to AI summary
      handleAIStatusSummary();
    } finally {
      setAutoPopulating(false);
    }
  };

  const handleAIStatusSummary = async () => {
    setAiLoading(true);
    setTimeout(() => {
      setFormData(prev => ({
        ...prev,
        bcm_status_summary: "The BCM program is operating at 78% maturity level. Key achievements this period include completion of 12 BIAs, 3 tabletop exercises, and rollout of updated crisis communication procedures. Areas requiring attention include training completion (currently at 65%) and pending risk assessments for new cloud infrastructure.",
        bia_status: "12 of 15 critical processes have completed BIAs (80%). 3 Finance processes pending due to staff changes.",
        exercise_status: "3 exercises completed. Next full-scale exercise scheduled for Q2. Tabletop frequency on track.",
        training_status: "65% completion rate across organization. Executive awareness training 100% complete. IT DR training needs attention.",
        compliance_status: "ISO 22301 readiness at 82%. Pre-audit gap analysis identified 4 minor non-conformities, all in remediation.",
      }));
      setAiLoading(false);
      toast.success("AI status summary generated!");
    }, 2000);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.date) {
      toast.error("Please fill in required fields");
      return;
    }
    
    setSubmitting(true);
    try {
      const reviewData = {
        ...formData,
        review_date: formData.date,
        decisions: formData.decisions_made?.split('\n').filter(d => d.trim()) || [],
        action_items: formData.action_items?.split('\n').filter(a => a.trim()).map(a => ({ action: a.trim(), status: "open" })) || [],
      };
      await api.post("/kpi/management-reviews", reviewData);
      toast.success("Management Review created!");
      setShowWizard(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to create review");
    } finally {
      setSubmitting(false);
    }
  };

  // Generated title/natural key for the AI-composed draft (same value used for dedup).
  const mrTitle = () =>
    `Management Review — ${new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}`;

  // COMPOSE pattern: all selected banner items are aggregated into ONE draft review,
  // not created as separate records. summary + key_metrics -> bcm_status_summary;
  // change_flags -> risk_changes. (Routing relies on the management_review-only label
  // tags added in AISuggestionsBanner.flatten — see scope-collision note.)
  const handleAccept = async (payload) => {
    const items = payload || [];
    const title = mrTitle();
    const titleKey = title.toLowerCase();
    if ((reviews || []).some((r) => (r.title || "").toLowerCase() === titleKey)) {
      return { created: 0, skipped: items.length, alreadyAccepted: true };
    }
    const summaryLines = [];
    const flagLines = [];
    for (const raw of items) {
      if (raw && typeof raw === "object") {
        const text = raw.name || raw.title || raw.label || raw.kpi_name ||
          Object.values(raw).find((v) => typeof v === "string") || "";
        if (raw.__mrKey === "change_flags") flagLines.push(text);
        else summaryLines.push(text);
        continue;
      }
      const s = String(raw ?? "");
      if (/^summary:\s*/i.test(s)) summaryLines.unshift(s.replace(/^summary:\s*/i, ""));
      else if (/^change flag —\s*/i.test(s)) flagLines.push(s.replace(/^change flag —\s*/i, ""));
      else if (/^metric —\s*/i.test(s)) summaryLines.push(s.replace(/^metric —\s*/i, ""));
      else summaryLines.push(s);
    }
    const bcm = summaryLines.filter(Boolean).map((l, i) => (i === 0 ? l : `- ${l}`)).join("\n");
    const risk = flagLines.filter(Boolean).map((l) => `- ${l}`).join("\n");
    const draft = {
      title,
      review_type: "periodic",
      date: new Date().toISOString(),
      review_date: new Date().toISOString(),
      status: "draft",
      bcm_status_summary: bcm,
      risk_changes: risk,
      source: "ai_suggestion",
    };
    try {
      await api.post("/kpi/management-reviews", draft);
      await fetchData();
      return { created: items.length, skipped: 0 };
    } catch (error) {
      return { created: 0, skipped: 0, error: extractErrorMessage(error, "Failed to create management review") };
    }
  };

  // Open the wizard pre-populated so a draft can be reviewed/continued.
  const openWizardForReview = (review) => {
    setFormData({
      review_type: review.review_type || "periodic",
      title: review.title || "",
      date: review.date || review.review_date || "",
      attendees: review.attendees || "",
      location: review.location || "",
      bcm_status_summary: review.bcm_status_summary || "",
      bia_status: review.bia_status || "",
      exercise_status: review.exercise_status || "",
      training_status: review.training_status || "",
      compliance_status: review.compliance_status || "",
      open_issues: review.open_issues || "",
      capa_status: review.capa_status || "",
      risk_changes: review.risk_changes || "",
      decisions_made: review.decisions_made || "",
      action_items: Array.isArray(review.action_items)
        ? review.action_items.map((a) => a.action || a).join("\n")
        : (review.action_items || ""),
      next_review_date: review.next_review_date || "",
      meeting_minutes: review.meeting_minutes || "",
      status: review.status || "draft",
    });
    setCurrentStep(1);
    setShowWizard(true);
  };

  const resetForm = () => {
    setFormData({
      review_type: "quarterly",
      title: "",
      date: "",
      attendees: "",
      location: "",
      bcm_status_summary: "",
      bia_status: "",
      exercise_status: "",
      training_status: "",
      compliance_status: "",
      open_issues: "",
      capa_status: "",
      risk_changes: "",
      decisions_made: "",
      action_items: "",
      next_review_date: "",
      meeting_minutes: "",
      status: "scheduled",
    });
    setCurrentStep(1);
  };

  const _titleCase = (s) => String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const getStatusInfo = (statusId) => STATUS_OPTIONS.find(s => s.id === statusId) || { id: statusId, name: _titleCase(statusId) || "Unknown", color: "bg-slate-100 text-slate-700" };
  const getTypeInfo = (typeId) => REVIEW_TYPES.find(t => t.id === typeId) || { id: typeId, name: _titleCase(typeId) || "Review", frequency: "" };

  // Stats
  const stats = {
    total: reviews.length,
    completed: reviews.filter(r => r.status === "completed").length,
    scheduled: reviews.filter(r => r.status === "scheduled" || r.status === "planned").length,
    totalDecisions: reviews.reduce((sum, r) => sum + (r.decisions_count || (r.decisions ? r.decisions.length : 0)), 0),
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="management-review-page">
      <AISuggestionsBanner
        module="management_review"
        onAccept={handleAccept}
        existingKeys={reviews.map((r) => (r.title || "").toLowerCase())}
        itemKeyOf={() => `Management Review — ${new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}`.toLowerCase()}
      />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-purple-100 text-purple-700">ACT Phase</Badge>
            <Badge variant="outline">ISO 22301 Clause 9.3</Badge>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Management Review</h1>
          <p className="text-slate-600 mt-1">
            Formal reviews of BCM program performance and effectiveness
          </p>
        </div>
        <Button 
          onClick={() => setShowWizard(true)}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="new-review-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Review
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ClipboardList className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Reviews</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Completed</p>
                <p className="text-2xl font-bold text-slate-900">{stats.completed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Scheduled</p>
                <p className="text-2xl font-bold text-slate-900">{stats.scheduled}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Target className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Decisions</p>
                <p className="text-2xl font-bold text-slate-900">{stats.totalDecisions}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Exercise Currency summary (Defect 4) — visible before any review exists */}
      {reviewMetrics?.exercise_currency && (
        <Card data-testid="mgmt-review-exercise-currency-summary">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-purple-600" /> Exercise Currency
            </CardTitle>
            <CardDescription>
              Required every {reviewMetrics.exercise_currency.required_frequency_months} months · {reviewMetrics.exercise_currency.threshold_source}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-center">
              <div><p className="text-xl font-bold text-slate-900">{reviewMetrics.exercise_currency.total_exercises}</p><p className="text-xs text-slate-500">Total</p></div>
              <div><p className="text-xl font-bold text-slate-900">{reviewMetrics.exercise_currency.completed}</p><p className="text-xs text-slate-500">Completed</p></div>
              <div><p className="text-xl font-bold text-emerald-600">{reviewMetrics.exercise_currency.current}</p><p className="text-xs text-slate-500">Current</p></div>
              <div><p className="text-xl font-bold text-amber-600">{reviewMetrics.exercise_currency.approaching_due}</p><p className="text-xs text-slate-500">Approaching</p></div>
              <div><p className="text-xl font-bold text-red-600">{reviewMetrics.exercise_currency.overdue}</p><p className="text-xs text-slate-500">Overdue</p></div>
              <div><p className="text-xl font-bold text-slate-500">{reviewMetrics.exercise_currency.never_tested}</p><p className="text-xs text-slate-500">Never Tested</p></div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reviews Table */}
      <Card>
        <CardHeader>
          <CardTitle>Management Review Log</CardTitle>
          <CardDescription>History of management reviews and decisions</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No management reviews yet</p>
              <Button className="mt-4" onClick={() => setShowWizard(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Schedule First Review
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Review</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Attendees</TableHead>
                  <TableHead>Decisions</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviews.map((review) => {
                  const statusInfo = getStatusInfo(review.status);
                  const typeInfo = getTypeInfo(review.review_type);
                  
                  return (
                    <TableRow key={review.id}>
                      <TableCell className="font-medium">{review.title}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{typeInfo.name}</Badge>
                      </TableCell>
                      <TableCell>{(review.date || review.review_date) ? new Date(review.date || review.review_date).toLocaleDateString() : 'N/A'}</TableCell>
                      <TableCell className="max-w-[150px] truncate">{review.attendees}</TableCell>
                      <TableCell>
                        <Badge className="bg-sky-100 text-sky-700">{review.decisions_count || (review.decisions ? review.decisions.length : 0)}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusInfo.color}>{statusInfo.name}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => setSelectedReview(review)} data-testid={`view-review-${review.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openWizardForReview(review)} data-testid={`edit-review-${review.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost">
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Review Detail Dialog */}
      <Dialog open={!!selectedReview} onOpenChange={() => setSelectedReview(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {selectedReview && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold" data-testid="review-detail-title">{selectedReview.title}</h2>
                <div className="flex items-center gap-3 mt-2">
                  <Badge className={
                    selectedReview.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                    selectedReview.status === "planned" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"
                  }>{selectedReview.status}</Badge>
                  <span className="text-sm text-slate-500">{selectedReview.review_type}</span>
                  <span className="text-sm text-slate-500">{new Date(selectedReview.review_date).toLocaleDateString()}</span>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><Users className="w-4 h-4" /> Attendees</h3>
                <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">{selectedReview.attendees || "Not specified"}</p>
              </div>

              {selectedReview.bcm_status_summary && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-sky-600" /> BCM Status Summary</h3>
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-line" data-testid="review-detail-bcm-summary">{selectedReview.bcm_status_summary}</p>
                </div>
              )}

              {selectedReview.risk_changes && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" /> Risk Changes</h3>
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-line" data-testid="review-detail-risk-changes">{selectedReview.risk_changes}</p>
                </div>
              )}

              {selectedReview.agenda_items?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Agenda Items</h3>
                  <ul className="space-y-1">
                    {selectedReview.agenda_items.map((item, i) => (
                      <li key={i} className="text-sm text-slate-600 flex items-start gap-2 bg-slate-50 p-2 rounded">
                        <span className="text-slate-400 font-mono text-xs mt-0.5">{i + 1}.</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedReview.decisions?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><Target className="w-4 h-4 text-amber-600" /> Decisions ({selectedReview.decisions.length})</h3>
                  <ul className="space-y-1">
                    {selectedReview.decisions.map((d, i) => (
                      <li key={i} className="text-sm text-slate-700 bg-amber-50 border border-amber-100 p-2 rounded flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedReview.action_items?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-violet-600" /> Action Items ({selectedReview.action_items.length})</h3>
                  <Table>
                    <TableHeader>
                      <TableRow><TableHead>Action</TableHead><TableHead>Assigned To</TableHead><TableHead>Due Date</TableHead><TableHead>Status</TableHead></TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedReview.action_items.map((ai, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-sm font-medium">{ai.action}</TableCell>
                          <TableCell className="text-sm">{ai.assigned_to}</TableCell>
                          <TableCell className="text-sm">{ai.due_date ? new Date(ai.due_date).toLocaleDateString() : "—"}</TableCell>
                          <TableCell>
                            <Badge className={
                              ai.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                              ai.status === "in_progress" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-700"
                            }>{ai.status}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {selectedReview.bcm_status && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-sky-600" /> BCM Programme Status</h3>
                  <div className="bg-sky-50 border border-sky-100 p-3 rounded-lg space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Overall Score:</span>
                      <span className="text-lg font-bold text-sky-700">{selectedReview.bcm_status.overall_score}%</span>
                      {selectedReview.bcm_status.trend === "up" && <TrendingUp className="w-4 h-4 text-emerald-500" />}
                    </div>
                    {selectedReview.bcm_status.highlights && (
                      <p className="text-sm text-slate-600">{selectedReview.bcm_status.highlights}</p>
                    )}
                  </div>
                </div>
              )}

              {selectedReview.next_review_date && (
                <div className="text-sm text-slate-500 border-t pt-3">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Next Review: {new Date(selectedReview.next_review_date).toLocaleDateString()}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Wizard Dialog */}
      <Dialog open={showWizard} onOpenChange={setShowWizard}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <WizardContainer
            title="Management Review"
            description="Document BCM program review meeting"
            badge="ISO 22301 Clause 9.3"
            steps={WIZARD_STEPS}
            currentStep={currentStep}
            onStepChange={setCurrentStep}
            onComplete={handleSubmit}
            isLoading={submitting}
            showSaveDraft={false}
          >
            {/* Step 1: Review Setup */}
            <WizardStep step={1} currentStep={currentStep}>
              <div className="space-y-6">
                {(orgFoundation.mission || orgFoundation.vision) && (
                  <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-4 space-y-3" data-testid="review-mission-vision-card">
                    <div className="flex items-center gap-2 text-indigo-800">
                      <Target className="w-4 h-4" />
                      <span className="text-sm font-semibold">Organization Mission &amp; Vision</span>
                      <Badge variant="outline" className="text-[10px] bg-white text-indigo-700 border-indigo-200">Pre-filled from Organization Profile</Badge>
                    </div>
                    {orgFoundation.mission && (
                      <div data-testid="review-mission-text">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-500 mb-0.5">Mission</p>
                        <p className="text-sm text-slate-700">{orgFoundation.mission}</p>
                      </div>
                    )}
                    {orgFoundation.vision && (
                      <div data-testid="review-vision-text">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-500 mb-0.5">Vision</p>
                        <p className="text-sm text-slate-700">{orgFoundation.vision}</p>
                      </div>
                    )}
                    <p className="text-[11px] text-indigo-500">Anchor this review's decisions against the organization's mission &amp; vision.</p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Review Type</Label>
                  <div className="grid grid-cols-2 gap-4">
                    {REVIEW_TYPES.map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, review_type: type.id })}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          formData.review_type === type.id
                            ? "border-purple-500 bg-purple-50"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <span className="font-semibold block">{type.name}</span>
                        <span className="text-sm text-slate-500">{type.frequency}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Review Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Q1 2025 Management Review"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="Meeting room or virtual"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Attendees</Label>
                  <Textarea
                    value={formData.attendees}
                    onChange={(e) => setFormData({ ...formData, attendees: e.target.value })}
                    placeholder="List attendees (one per line or comma-separated)"
                    rows={3}
                  />
                </div>
              </div>
            </WizardStep>

            {/* Step 2: BCM Status */}
            <WizardStep step={2} currentStep={currentStep}>
              <div className="space-y-6">
                <div className="flex justify-between items-center gap-4">
                  <div className="flex-1">
                    <p className="text-sm text-slate-600">
                      Auto-populate BCM metrics from system data or generate AI summary
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      onClick={handleAutoPopulateMetrics}
                      disabled={autoPopulating}
                      data-testid="auto-populate-metrics-btn"
                      className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200 hover:border-emerald-300"
                    >
                      {autoPopulating ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <BarChart3 className="w-4 h-4 mr-2 text-emerald-600" />
                      )}
                      Auto-Populate from System
                    </Button>
                    <AIAutoFillButton
                      onAutoFill={handleAIStatusSummary}
                      isLoading={aiLoading}
                      label="AI Summary"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>BCM Program Status Summary</Label>
                  <Textarea
                    value={formData.bcm_status_summary}
                    onChange={(e) => setFormData({ ...formData, bcm_status_summary: e.target.value })}
                    data-testid="bcm-status-summary-textarea"
                    placeholder="Overall BCM program status and highlights..."
                    rows={4}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>BIA Status</Label>
                    <Textarea
                      value={formData.bia_status}
                      onChange={(e) => setFormData({ ...formData, bia_status: e.target.value })}
                      placeholder="BIA completion and updates..."
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Exercise Status</Label>
                    <Textarea
                      value={formData.exercise_status}
                      onChange={(e) => setFormData({ ...formData, exercise_status: e.target.value })}
                      placeholder="Exercise completion and findings..."
                      rows={2}
                    />
                    {reviewMetrics?.exercise_currency && (
                      <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs" data-testid="mgmt-review-exercise-currency">
                        <p className="font-semibold text-slate-700 mb-1">Exercise Currency</p>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-slate-600">
                          <span>Total: {reviewMetrics.exercise_currency.total_exercises}</span>
                          <span>Completed: {reviewMetrics.exercise_currency.completed}</span>
                          <span className="text-emerald-600">Current: {reviewMetrics.exercise_currency.current}</span>
                          <span className="text-amber-600">Approaching due: {reviewMetrics.exercise_currency.approaching_due}</span>
                          <span className="text-red-600">Overdue: {reviewMetrics.exercise_currency.overdue}</span>
                          <span className="text-slate-500">Never tested: {reviewMetrics.exercise_currency.never_tested}</span>
                        </div>
                        <p className="mt-1 text-slate-500">
                          Required every {reviewMetrics.exercise_currency.required_frequency_months} months · {reviewMetrics.exercise_currency.threshold_source}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Training Status</Label>
                    <Textarea
                      value={formData.training_status}
                      onChange={(e) => setFormData({ ...formData, training_status: e.target.value })}
                      placeholder="Training completion rates..."
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Compliance Status</Label>
                    <Textarea
                      value={formData.compliance_status}
                      onChange={(e) => setFormData({ ...formData, compliance_status: e.target.value })}
                      placeholder="ISO 22301 compliance status..."
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            </WizardStep>

            {/* Step 3: Issues & Actions */}
            <WizardStep step={3} currentStep={currentStep}>
              <div className="space-y-6">
                <AIInlineSuggestion
                  type="tip"
                  suggestion="Review open CAPAs, outstanding audit findings, and risk register changes since the last management review."
                />
                
                <div className="space-y-2">
                  <Label>Open Issues</Label>
                  <Textarea
                    value={formData.open_issues}
                    onChange={(e) => setFormData({ ...formData, open_issues: e.target.value })}
                    placeholder="Outstanding issues requiring management attention..."
                    rows={4}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>CAPA Status</Label>
                  <Textarea
                    value={formData.capa_status}
                    onChange={(e) => setFormData({ ...formData, capa_status: e.target.value })}
                    placeholder="Status of corrective and preventive actions..."
                    rows={3}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Risk Changes</Label>
                  <Textarea
                    value={formData.risk_changes}
                    onChange={(e) => setFormData({ ...formData, risk_changes: e.target.value })}
                    data-testid="risk-changes-textarea"
                    placeholder="New risks, changed risks, emerging threats..."
                    rows={3}
                  />
                </div>
              </div>
            </WizardStep>

            {/* Step 4: Decisions */}
            <WizardStep step={4} currentStep={currentStep}>
              <div className="space-y-6">
                <AIInlineSuggestion
                  type="info"
                  suggestion="Document all decisions made by management. These become the basis for improvement actions and resource allocation."
                />
                
                <div className="space-y-2">
                  <Label>Decisions Made</Label>
                  <Textarea
                    value={formData.decisions_made}
                    onChange={(e) => setFormData({ ...formData, decisions_made: e.target.value })}
                    placeholder="List each decision on a new line..."
                    rows={6}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Action Items</Label>
                  <Textarea
                    value={formData.action_items}
                    onChange={(e) => setFormData({ ...formData, action_items: e.target.value })}
                    placeholder="Actions arising from this review..."
                    rows={4}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Next Review Date</Label>
                  <Input
                    type="date"
                    value={formData.next_review_date}
                    onChange={(e) => setFormData({ ...formData, next_review_date: e.target.value })}
                  />
                </div>
              </div>
            </WizardStep>

            {/* Step 5: Minutes */}
            <WizardStep step={5} currentStep={currentStep}>
              <div className="space-y-6">
                <AIInlineSuggestion
                  type="info"
                  suggestion="Meeting minutes will be generated from the information entered. Review and add any additional notes."
                />
                
                <div className="space-y-2">
                  <Label>Meeting Minutes / Additional Notes</Label>
                  <Textarea
                    value={formData.meeting_minutes}
                    onChange={(e) => setFormData({ ...formData, meeting_minutes: e.target.value })}
                    placeholder="Additional notes, discussions, or context..."
                    rows={8}
                  />
                </div>
                
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-2">Review Summary</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Type:</span>
                      <span className="ml-2 font-medium">{getTypeInfo(formData.review_type)?.name}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Date:</span>
                      <span className="ml-2 font-medium">{formData.date || "Not set"}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Decisions:</span>
                      <span className="ml-2 font-medium">
                        {formData.decisions_made?.split('\n').filter(d => d.trim()).length || 0}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500">Next Review:</span>
                      <span className="ml-2 font-medium">{formData.next_review_date || "Not set"}</span>
                    </div>
                  </div>
                </div>
              </div>
            </WizardStep>
          </WizardContainer>
        </DialogContent>
      </Dialog>
    </div>
  );
}
